"use client";

import {
  Chip,
  type ChipOption,
  SubmittedChips,
  shouldStackChips,
  toggleChipSelection,
  useStylesConfigValue,
} from "@health/ui";
import { cn } from "@health/ui/utils";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAnalyticsContext } from "../analytics/context";
import { useTrack } from "../analytics/useTrack";
import { useTurnTiming } from "../analytics/useTurnTiming";
import { ChipsWidgetSkeleton } from "./ChipsWidget.skeleton";

const PROBE_DELIMITER = "__probe_";

function isProbeChip(id: string): boolean {
  return id.includes(PROBE_DELIMITER);
}

function getProbeArchetype(id: string): string | null {
  const idx = id.indexOf(PROBE_DELIMITER);
  return idx !== -1 ? id.slice(idx + PROBE_DELIMITER.length) : null;
}

function getProbeOriginalId(id: string): string | null {
  const idx = id.indexOf(PROBE_DELIMITER);
  return idx !== -1 ? id.slice(0, idx) : null;
}

function buildProbeTooltip(probeId: string, allOptions: ChipOption[]): string {
  const archetype = getProbeArchetype(probeId);
  const originalId = getProbeOriginalId(probeId);
  const original = allOptions.find((o) => o.id === originalId);
  const lines = [`Archetype probe: ${archetype}`];
  if (original) {
    lines.push(`Duplicates: "${original.label}"`);
  }
  lines.push(`ID: ${probeId}`);
  return lines.join("\n");
}

function useDebugMode(): boolean {
  return useMemo(() => {
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).get("debug") === "1";
  }, []);
}

type Props = {
  input: Record<string, unknown>;
  output?: unknown;
  onSubmit: (result: unknown) => void;
  submitted?: boolean;
  demoMode?: boolean;
  streaming?: boolean;
};

const isCatchAllPhrase = (label?: string) => {
  return (
    label?.toLowerCase().startsWith("something else") || label?.toLowerCase().startsWith("other")
  );
};

/** Dedupe chip:view_step across StrictMode remounts + parent re-renders. */
const CHIP_VIEW_EMITTED: Set<string> = /* @__PURE__ */ new Set<string>();

export function ChipsWidget({ input, output, onSubmit, submitted, streaming }: Props) {
  const options = Array.isArray(input.options) ? (input.options as ChipOption[]) : [];
  const multiSelect = (input.multi_select as boolean) ?? false;
  const includeFallback = (input.include_fallback as boolean) ?? true;
  const showInstruction = (input.show_instruction as boolean) ?? false;
  const ariaLabel = "Select an option";
  const debugMode = useDebugMode();
  const fieldId = typeof input.field_id === "string" ? input.field_id : undefined;
  const groupId = typeof input.group_id === "string" ? input.group_id : undefined;
  const track = useTrack();

  // Analytics: chip:view_step once per (fieldId + toolCallId) pair. Fired once
  // on first non-streaming render. Server re-derives is_medical from the spec
  // and hashes the field name if medical — client-supplied is_medical is a
  // hint only (defaults to true — server decides).
  const toolCallId =
    typeof input._toolCallId === "string" ? (input._toolCallId as string) : undefined;
  useEffect(() => {
    if (streaming || submitted || !fieldId) return;
    const key = `${toolCallId ?? ""}:${fieldId}`;
    if (CHIP_VIEW_EMITTED.has(key)) return;
    CHIP_VIEW_EMITTED.add(key);
    track("chip:view_step", {
      field: fieldId,
      group_id: groupId,
      is_medical: true,
    });
    // Intentionally only depends on stable IDs. Streaming -> ready is a
    // one-way transition inside the same mounted widget.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // prevent duplicate catch-all phrase options. Which can happen when 'Something else'/'Other' is included in option, and we add again bcz of include_fallback.
  // handled in prompt as well, but this is kept as a fallback.
  const catchAllPhraseOption = options.find((opt) => isCatchAllPhrase(opt.label));

  const allOptions: ChipOption[] = [
    ...options.filter(
      (opt) => opt.id && opt.label && !(includeFallback && opt === catchAllPhraseOption),
    ),
    ...(!streaming && includeFallback
      ? [{ id: "__fallback__", label: catchAllPhraseOption?.label || "Something else" }]
      : []),
  ];

  // useState must be above any conditional return (Rules of Hooks)
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Focus the first interactive chip when the widget is first ready (not
  // streaming, not submitted, has options). Keyboard + screen-reader users
  // land on the new question without having to tab through history.
  // `preventScroll: true` — scroll-snap / in-view hooks own positioning.
  //
  // Wave 0.3 TTFC: same rAF tick as focus lands, emit the turn-timer boundary.
  // This fuses focus-management with TTFC measurement so t1 === patient-felt
  // interactive moment. cancelAnimationFrame in the cleanup protects against
  // a StrictMode double-invoke firing a post-unmount track() call.
  const groupRef = useRef<HTMLDivElement>(null);
  const didFocusRef = useRef(false);
  const readyForFocus = !streaming && !submitted && allOptions.length > 0;
  const { markWidgetReady, markAnyWidgetReady } = useTurnTiming();
  const analytics = useAnalyticsContext();
  const traceMode = useStylesConfigValue("traceMode");
  useEffect(() => {
    if (!readyForFocus || didFocusRef.current) return;
    if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
    const raf = requestAnimationFrame(() => {
      const firstChip = groupRef.current?.querySelector<HTMLElement>(
        '[role="radio"], [role="checkbox"], button',
      );
      if (!firstChip) return;
      didFocusRef.current = true;
      firstChip.focus({ preventScroll: true });
      const variantId = analytics?.variantId;
      const brand = analytics?.site ? { brand: analytics.site } : {};
      const traceModeTag =
        traceMode === "plan" || traceMode === "disabled" || traceMode === "deliberate"
          ? { traceMode }
          : {};

      const result = markWidgetReady({ widgetType: "chips" });
      if (result && variantId) {
        track("latency:time_to_first_chip", {
          durationMs: result.durationMs,
          turnIndex: result.turnIndex,
          trigger: result.trigger,
          widgetType: "chips",
          capture_source: "browser",
          variantId,
          ...brand,
          ...traceModeTag,
        });
      }

      // Broader perceived-latency boundary — independent dedupe bit in the
      // turn registry so this emission is not suppressed by the TTFC emit.
      const anyResult = markAnyWidgetReady({ widgetType: "chips" });
      if (anyResult && variantId) {
        track("latency:user_to_first_widget_ms", {
          durationMs: anyResult.durationMs,
          turnIndex: anyResult.turnIndex,
          trigger: anyResult.trigger,
          widgetType: "chips",
          capture_source: "browser",
          variantId,
          ...brand,
          ...traceModeTag,
        });
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [
    readyForFocus,
    markWidgetReady,
    markAnyWidgetReady,
    track,
    analytics?.variantId,
    analytics?.site,
    traceMode,
  ]);

  if (!allOptions.length && !streaming) return null;

  // Read-only mode: show what was selected
  if (submitted && output) {
    const result = output as { selected: string[]; fallback_selected: boolean };
    return (
      <SubmittedChips
        allOptions={allOptions}
        selectedIds={new Set(result.selected)}
        fallbackSelected={result.fallback_selected}
        multiSelect={multiSelect}
      />
    );
  }

  const shouldStack = shouldStackChips({ multi_select: multiSelect, options: allOptions });

  // Streaming mode: keep the radiogroup wrapper mounted across the empty ->
  // first-chip transition so React reconciles in place (no skeleton
  // unmount/remount flash). The wrapper carries `aria-busy` either way, so
  // screen readers are notified during the empty window too.
  if (streaming) {
    return (
      <div className="w-full space-y-3">
        <div
          role={multiSelect ? "group" : "radiogroup"}
          aria-label={ariaLabel}
          aria-busy
          className={cn(
            "flex items-start flex-wrap gap-2",
            (shouldStack || multiSelect) && "flex-col gap-2.5",
          )}
        >
          {allOptions.length === 0 ? (
            <ChipsWidgetSkeleton />
          ) : (
            allOptions.map((opt) => (
              <Chip
                key={opt.id}
                label={opt.label}
                disabled
                variant={multiSelect ? "multi-select" : "single-select"}
                role={multiSelect ? "checkbox" : "radio"}
                aria-checked={false}
              />
            ))
          )}
        </div>
      </div>
    );
  }

  const handleSelect = (id: string) => {
    if (!multiSelect) {
      const fallbackSelected = id === "__fallback__";
      const selectedId = fallbackSelected ? [] : [id];
      if (fieldId) {
        track("chip:answer", {
          field: fieldId,
          group_id: groupId,
          is_medical: true,
          type: "click",
          response: selectedId,
        });
      }
      onSubmit({
        selected: selectedId,
        fallback_selected: fallbackSelected,
      });
      return;
    }

    setSelected((prev) => toggleChipSelection(prev, id, allOptions));
  };

  const handleSubmitMulti = () => {
    const fallbackSelected = selected.has("__fallback__");
    const selectedIds = Array.from(selected).filter((id) => id !== "__fallback__");
    if (fieldId) {
      track("chip:answer", {
        field: fieldId,
        group_id: groupId,
        is_medical: true,
        type: "multi-select",
        response: selectedIds,
      });
    }
    onSubmit({
      selected: selectedIds,
      fallback_selected: fallbackSelected,
    });
  };

  // Multi-select: vertical stacked list with checkboxes
  if (multiSelect) {
    const hintId = "chips-hint";
    return (
      <div className="w-full space-y-3">
        <p id={hintId} className="text-sm text-text-agent">
          (Select all that apply)
        </p>
        <div
          ref={groupRef}
          role="group"
          aria-label={ariaLabel}
          aria-describedby={hintId}
          className="space-y-2.5"
        >
          {allOptions.map((opt) => {
            const isSelected = selected.has(opt.id);
            const isFallback = opt.id === "__fallback__";
            const probe = debugMode && isProbeChip(opt.id);
            const tooltip = probe ? buildProbeTooltip(opt.id, allOptions) : undefined;
            return (
              <div key={opt.id} className="relative" title={tooltip}>
                <Chip
                  label={opt.label}
                  selected={isSelected}
                  secondary={isFallback}
                  variant="multi-select"
                  role="checkbox"
                  aria-checked={isSelected}
                  onClick={() => handleSelect(opt.id)}
                  probeHighlight={probe}
                />
              </div>
            );
          })}
        </div>
        {selected.size > 0 && (
          <button
            onClick={handleSubmitMulti}
            className="block mt-4 w-content cursor-pointer rounded-full bg-checkbox-filled hover:bg-accent/80 py-4 px-9 text-sm font-semibold text-white transition-all active:scale-[0.98] focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
          >
            Continue
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="w-full space-y-3">
      <div
        ref={groupRef}
        role="radiogroup"
        aria-label={ariaLabel}
        className={cn("flex items-start flex-wrap gap-2", shouldStack && "flex-col gap-2.5")}
      >
        {showInstruction && (
          <p className="text-sm text-text-agent">(Choose the option that fits best)</p>
        )}

        {allOptions.map((opt) => {
          const isFallback = opt.id === "__fallback__";
          const probe = debugMode && isProbeChip(opt.id);
          const tooltip = probe ? buildProbeTooltip(opt.id, allOptions) : undefined;
          return (
            <div key={opt.id} className="relative" title={tooltip}>
              <Chip
                label={opt.label}
                secondary={isFallback}
                variant="single-select"
                role="radio"
                aria-checked={false}
                onClick={() => handleSelect(opt.id)}
                probeHighlight={probe}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
